import {
	JOURNEY_DEFINITION_IDS,
	JOURNEY_DEFINITION_STATUSES,
	PRODUCT_KEYS,
	WORKFLOW_TEMPLATE_IDS,
	questionnaire,
	type JourneyDefinition,
} from "@optimist/workflow-engine"
import { z } from "zod"

export const ProductKeySchema = z.enum(PRODUCT_KEYS).meta({ id: "ProductKey" })

export const WorkflowTemplateIdSchema = z.enum(WORKFLOW_TEMPLATE_IDS).meta({
	id: "WorkflowTemplateId",
})

export const JourneyDefinitionIdSchema = z.enum(JOURNEY_DEFINITION_IDS).meta({
	id: "JourneyDefinitionId",
})

export const JourneyDefinitionStatusSchema = z.enum(JOURNEY_DEFINITION_STATUSES).meta({
	id: "JourneyDefinitionStatus",
})

export const WorkflowTemplateReferenceSchema = z
	.strictObject({
		id: WorkflowTemplateIdSchema,
		version: z.number().int().positive(),
	})
	.meta({ id: "WorkflowTemplateReference" })

export const QuestionnaireTemplateReferenceSchema = z
	.strictObject({
		id: z.enum(questionnaire.templateIds),
		version: z.string().min(1),
	})
	.meta({ id: "QuestionnaireTemplateReference" })

export const JourneyDefinitionSchema: z.ZodType<JourneyDefinition> = z
	.strictObject({
		id: JourneyDefinitionIdSchema,
		version: z.number().int().positive(),
		status: JourneyDefinitionStatusSchema,
		productKey: ProductKeySchema,
		workflow: WorkflowTemplateReferenceSchema,
		questionnaire: QuestionnaireTemplateReferenceSchema,
	})
	.meta({ id: "JourneyDefinition" })

export const JourneyAssignmentTokenPayloadSchema = z
	.strictObject({
		tokenVersion: z.literal(1),
		definitionId: JourneyDefinitionIdSchema,
		definitionVersion: z.number().int().positive(),
		productKey: ProductKeySchema,
		issuedAt: z.number().int().nonnegative(),
		expiresAt: z.number().int().positive(),
		iss: z.string().min(1).optional(),
		aud: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]).optional(),
		iat: z.number().int().nonnegative().optional(),
		exp: z.number().int().positive().optional(),
	})
	.refine((payload) => payload.expiresAt > payload.issuedAt, {
		error: "Journey assignment token expiry must be after its issue time.",
		path: ["expiresAt"],
	})
	.superRefine((payload, context) => {
		if (payload.iat !== undefined && payload.iat !== payload.issuedAt) {
			context.addIssue({
				code: "custom",
				message: "Journey assignment issue claims do not match.",
				path: ["iat"],
			})
		}
		if (payload.exp !== undefined && payload.exp !== payload.expiresAt) {
			context.addIssue({
				code: "custom",
				message: "Journey assignment expiry claims do not match.",
				path: ["exp"],
			})
		}
	})
	.meta({ id: "JourneyAssignmentTokenPayload" })

export type JourneyAssignmentTokenPayload = z.infer<
	typeof JourneyAssignmentTokenPayloadSchema
>

export const JourneyAssignmentResponseSchema = z
	.strictObject({
		definitionId: JourneyDefinitionIdSchema,
		assignmentToken: z.string().min(1),
	})
	.meta({ id: "JourneyAssignmentResponse" })

export type JourneyAssignmentResponse = z.infer<typeof JourneyAssignmentResponseSchema>
